/**
 * Source control in Settings: the organisation's connections to GitHub,
 * Azure DevOps and Jira (administrators), and each person's own linked accounts.
 */

import type { SourceConnection, SourceIdentity, SourceProvider } from '@aioe/shared';
import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { API_URL, routes, type SourceConnectionCreate } from '../api/client.ts';
import { isCloud } from '../edition.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { Button, ChoiceSelect, ErrorNote, inputClass, ListBlock, ListRow, PlainPanel, StatusMark, Tick } from './ui.tsx';

export const PROVIDER_TEXT: Record<SourceProvider, string> = { github: 'GitHub', 'azure-devops': 'Azure DevOps', jira: 'Jira' };

/** `https://mojoup.atlassian.net` → `mojoup`, the name a Jira connection is known by. */
const siteName = (url: string) => {
  try {
    return new URL(url).hostname.split('.')[0] ?? '';
  } catch {
    return '';
  }
};

/** Settings → Source connections. Only administrators see it (the API refuses everyone else). */
export function SourceConnectionsPanel() {
  const list = useLoad(() => routes.sourceConnections().catch(() => undefined), []);
  const [error, setError] = useState<string>();
  const [provider, setProvider] = useState<SourceProvider>('github');
  const [name, setName] = useState('');
  const [organisation, setOrganisation] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [appId, setAppId] = useState('');
  const [appSlug, setAppSlug] = useState('');
  const [appKey, setAppKey] = useState('');
  const [shared, setShared] = useState(false);
  if (!list.data) return null;
  const run = async (work: () => Promise<unknown>) => {
    setError(undefined);
    try {
      await work();
      list.reload();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const jira = provider === 'jira';
  const create = () => {
    const body: SourceConnectionCreate = { provider, name: name.trim(), organisation: jira ? siteName(baseUrl.trim()) : organisation.trim(), allowSharedCredential: jira ? false : shared };
    if (baseUrl.trim()) body.baseUrl = baseUrl.trim();
    if (token.trim()) body.token = token.trim();
    if (jira && email.trim()) body.email = email.trim();
    if (provider === 'github' && clientId.trim()) {
      body.oauthClientId = clientId.trim();
      body.oauthClientSecret = clientSecret.trim();
    }
    if (provider === 'github' && appId.trim()) {
      body.appId = appId.trim();
      body.appSlug = appSlug.trim() || undefined;
      body.appPrivateKey = appKey.trim();
    }
    return run(async () => {
      await routes.createSourceConnection(body);
      setName('');
      setOrganisation('');
      setBaseUrl('');
      setToken('');
      setEmail('');
      setClientId('');
      setClientSecret('');
      setAppId('');
      setAppSlug('');
      setAppKey('');
    });
  };
  return (
    <PlainPanel
      id="source-connections"
      title="Source connections"
      description={`Where the organisation's code lives. People commit with their own linked accounts. For GitHub, register a GitHub App on the organisation and enter it here: people then connect through it and the organisation's reads use its installation, with no tokens anywhere.${isCloud() ? '' : ' For Azure DevOps, people connect through Entra.'} A Jira connection serves issue trackers only: projects keep a board in step with a Jira project through it.`}
    >
      {list.data.map((c) => (
        <ConnectionRow key={c.id} c={c} onChange={list.reload} onError={setError} />
      ))}
      <div className="grid gap-2">
        <div className="text-sm font-medium">Add a connection</div>
        <div className="flex flex-wrap gap-2">
          <ChoiceSelect label="Provider" value={provider} options={{ github: 'GitHub', ...(isCloud() ? {} : { 'azure-devops': 'Azure DevOps' }), jira: 'Jira' } as Record<SourceProvider, string>} onChange={setProvider} />
          <input aria-label="Name" className={`${inputClass} max-w-48`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" />
          {!jira && <input aria-label="Organisation" className={`${inputClass} max-w-56`} value={organisation} onChange={(e) => setOrganisation(e.target.value)} placeholder={provider === 'github' ? 'GitHub organisation' : 'DevOps organisation'} />}
        </div>
        {jira ? (
          <div className="grid gap-2">
            <span className="text-xs text-muted">The Jira site, and an API token for an account that can see and edit the projects (create one at id.atlassian.com, Security, API tokens). Issues the platform writes show as that account.</span>
            <input aria-label="Jira site" className={`${inputClass} mono`} value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="Jira site (https://your-site.atlassian.net)" autoComplete="off" />
            <div className="grid gap-2 sm:grid-cols-2">
              <input aria-label="Account email" className={inputClass} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="The account's email" autoComplete="off" />
              <input aria-label="API token" className={`${inputClass} mono`} type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="API token" autoComplete="off" />
            </div>
          </div>
        ) : (
          <input aria-label="Base URL" className={`${inputClass} mono`} value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder={provider === 'github' ? 'API base, only for GitHub Enterprise Server (https://ghe.example.com/api/v3)' : 'Only for Azure DevOps Server (https://devops.example.com)'} />
        )}
        {provider === 'github' && (
          <div className="grid gap-2">
            <span className="text-xs text-muted">GitHub App (from the app's settings page; its callback URL is the API's /source/v1/github/callback)</span>
            <div className="grid gap-2 sm:grid-cols-2">
              <input aria-label="App id" className={`${inputClass} mono`} value={appId} onChange={(e) => setAppId(e.target.value)} placeholder="App ID" autoComplete="off" />
              <input aria-label="App slug" className={`${inputClass} mono`} value={appSlug} onChange={(e) => setAppSlug(e.target.value)} placeholder="App slug (from its URL)" autoComplete="off" />
              <input aria-label="Client id" className={`${inputClass} mono`} value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="Client ID" autoComplete="off" />
              <input aria-label="Client secret" className={`${inputClass} mono`} type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} placeholder="Client secret" autoComplete="off" />
            </div>
            <textarea aria-label="Private key" className={`${inputClass} mono min-h-20 text-xs`} value={appKey} onChange={(e) => setAppKey(e.target.value)} placeholder="Private key (PEM), generated on the app's settings page" spellCheck={false} />
          </div>
        )}
        {!jira && (
          <>
            <details className="text-xs text-muted">
              <summary className="cursor-[var(--cursor-interactive)]">Or an organisation token (not recommended)</summary>
              <input aria-label="Organisation token" className={`${inputClass} mono mt-2`} type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="A personal access token for reads by people who have not linked" autoComplete="off" />
            </details>
            <Tick checked={shared} onChange={setShared}>
              Let nodes fall back to the organisation's credential for work with nobody attached, or people who have not linked
            </Tick>
          </>
        )}
        <div>
          <Button size="sm" variant="secondary" isDisabled={!name.trim() || (jira ? !siteName(baseUrl.trim()) || !email.trim() || token.trim().length < 8 : !organisation.trim()) || (provider === 'github' && Boolean(clientId.trim()) !== Boolean(clientSecret.trim()))} onPress={() => void create()}>
            Add connection
          </Button>
        </div>
        <ErrorNote error={error} />
      </div>
    </PlainPanel>
  );
}

function ConnectionRow({ c, onChange, onError }: { c: SourceConnection; onChange: () => void; onError: (e?: string) => void }) {
  const [checking, setChecking] = useState(false);
  const [webhook, setWebhook] = useState<{ secret: string; url: string }>();
  const webhookUrl = `${API_URL}/source/v1/webhooks/${encodeURIComponent(c.id)}/${c.provider}`;
  /** A fresh secret, saved on the connection and shown once: GitHub, Azure DevOps or Jira gets the same one. */
  const setUpWebhooks = async () => {
    onError(undefined);
    const bytes = new Uint8Array(24);
    crypto.getRandomValues(bytes);
    const secret = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    try {
      await routes.patchSourceConnection(c.id, { webhookSecret: secret });
      setWebhook({ secret, url: webhookUrl });
      onChange();
    } catch (e) {
      onError((e as Error).message);
    }
  };
  const check = async () => {
    setChecking(true);
    onError(undefined);
    try {
      await routes.checkSourceConnection(c.id);
      onChange();
    } catch (e) {
      onError((e as Error).message);
      onChange();
    } finally {
      setChecking(false);
    }
  };
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <span className="min-w-0 flex-1 basis-64">
        <span className="font-medium">{c.name}</span>
        <span className="text-muted">
          {' '}
          · {PROVIDER_TEXT[c.provider]} · {c.organisation}
        </span>
        <span className="block text-xs text-muted">
          {c.provider === 'jira'
            ? `${c.baseUrl.replace(/^https?:\/\//, '')} · ${c.hasToken ? (c.account ? `API token as ${c.account}` : 'API token') : 'No API token'} · for issue trackers`
            : c.appId
              ? c.installationId
                ? `GitHub App installed on ${c.organisation}`
                : 'GitHub App not installed on the organisation yet'
              : c.hasToken
                ? c.account
                  ? `Organisation token as ${c.account}`
                  : 'Organisation token'
                : 'No organisation credential'}
          {c.oauthClientId ? ' · people connect with GitHub' : ''}
          {c.allowSharedCredential ? ' · shared fallback allowed' : ''}
          {c.hasWebhookSecret ? ' · webhooks set up' : ''}
          {c.lastCheckedAt ? ` · checked ${timeAgo(c.lastCheckedAt)}` : ''}
        </span>
        {c.lastError && <StatusMark state="error" label={c.lastError} />}
        {webhook && (
          <span className="mt-2 block rounded-lg border border-separator p-3 text-xs">
            <span className="block font-medium text-foreground">Webhook secret, shown once</span>
            <span className="mono block break-all select-all">{webhook.secret}</span>
            <span className="mt-2 block font-medium text-foreground">Webhook address</span>
            <span className="mono block break-all select-all">{webhook.url}</span>
            <span className="mt-2 block text-muted">
              {c.provider === 'github'
                ? 'In the GitHub App settings (or the repository\'s webhooks): paste both, content type application/json, and subscribe to Pull requests, Pull request reviews, Check suites and Check runs.'
                : c.provider === 'jira'
                  ? 'In Jira, Settings, System, WebHooks: create a webhook to this address with this secret, for Issue created, Issue updated and Comment created. A Jira automation rule can send to it too, with the secret in an X-AIOE-Webhook-Secret header.'
                  : 'In Azure DevOps service hooks: a web hook per event (pull request created, updated, merged) to this address, basic auth with any user name and this secret as the password.'}
            </span>
          </span>
        )}
      </span>
      <span className="flex gap-2">
        <Button size="sm" variant="ghost" isPending={checking} onPress={() => void check()}>
          Check
        </Button>
        <Button size="sm" variant="ghost" onPress={() => void setUpWebhooks()}>
          {c.hasWebhookSecret ? 'New webhook secret' : 'Set up webhooks'}
        </Button>
        <Button size="sm" variant="ghost" onPress={() => void routes.deleteSourceConnection(c.id).then(onChange, (e: Error) => onError(e.message))}>
          Remove
        </Button>
      </span>
    </div>
  );
}

/** Where `GET /people/v1/me` `profileUrl` lands (`/settings#linked-accounts`): a workbench sends a person here to link their accounts. */
export const LINKED_ACCOUNTS_ANCHOR = 'linked-accounts';

/**
 * Settings → You → Your Linked Accounts: the person's own linked accounts, one per provider, at
 * `/settings#linked-accounts` (which opens Settings → Linked accounts). Jira shows when the organisation connected Jira (a token with the
 * account's email). The organisation's trackers assign work by these (people.ts `linked`).
 */
export function SourceIdentitiesPanel() {
  const identities = useLoad(() => routes.sourceIdentities().catch(() => undefined), []);
  const connections = useLoad(() => routes.sourceConnections().catch(() => [] as SourceConnection[]), []);
  const [params, setParams] = useSearchParams();
  const { hash } = useLocation();
  const [error, setError] = useState<string>(params.get('link_failed') ? `Linking failed: ${params.get('link_failed')}` : '');
  const [tokens, setTokens] = useState<Record<string, string>>({});
  const [jiraEmail, setJiraEmail] = useState('');
  const loaded = Boolean(identities.data);
  // Arrived from a workbench's link: bring the panel into view once it has drawn.
  useEffect(() => {
    if (loaded && hash === `#${LINKED_ACCOUNTS_ANCHOR}`) document.getElementById(LINKED_ACCOUNTS_ANCHOR)?.scrollIntoView({ block: 'start' });
  }, [loaded, hash]);
  useEffect(() => {
    if (params.get('linked') || params.get('link_failed')) setParams({}, { replace: true });
    // The GitHub sign-in came back with a ticket: only this signed-in person can turn it into a link.
    const ticket = params.get('github_link');
    if (ticket) {
      setParams({}, { replace: true });
      routes
        .githubLinkFinish(ticket)
        .then(() => identities.reload())
        .catch((e) => setError(`Linking failed: ${(e as Error).message}`));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!identities.data) return null;
  const byProvider = new Map(identities.data.map((i) => [i.provider, i]));
  const conn = (p: SourceProvider) => (connections.data ?? []).find((c) => c.provider === p);
  const run = async (work: () => Promise<unknown>) => {
    setError('');
    try {
      await work();
      identities.reload();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const connectGithub = async (c: SourceConnection) => {
    const { url } = await routes.githubConnectUrl(c.id, `${window.location.origin}/settings/accounts`);
    window.location.assign(url);
  };
  const providers: SourceProvider[] = isCloud() ? ['github'] : ['github', 'azure-devops', ...(conn('jira') || byProvider.has('jira') ? (['jira'] as const) : [])];
  return (
    <div id={LINKED_ACCOUNTS_ANCHOR} className="scroll-mt-4">
      <PlainPanel
        title="Your linked accounts"
        description={
          isCloud()
            ? 'Your own account. Agents working for you commit as you, and reads show what you may see. Connect through GitHub where the organisation set that up; nothing is pasted and the platform refreshes the sign-in itself.'
            : 'Your own accounts. Agents working for you commit as you, and reads show what you may see. Connect through GitHub or Entra where the organisation set that up; nothing is pasted and the platform refreshes the sign-in itself.'
        }
      >
        {providers.map((p) => {
          const i: SourceIdentity | undefined = byProvider.get(p);
          const c = conn(p);
          return (
            <ListRow
              key={p}
              title={PROVIDER_TEXT[p]}
              description={
                i ? (
                  <>
                    Linked as {i.account}
                    {i.accountId && p === 'azure-devops' ? ` (${i.accountId})` : ''} · {i.via === 'token' ? 'personal token' : i.via === 'app' && p === 'azure-devops' ? 'through Entra' : 'signed in'} · {timeAgo(i.linkedAt)}
                  </>
                ) : (
                  'Not linked'
                )
              }
              control={
                i ? (
                  <Button size="sm" variant="ghost" onPress={() => void run(() => routes.unlinkSourceIdentity(p))}>
                    Unlink
                  </Button>
                ) : undefined
              }
            >
              {!i && (
                <div className="flex flex-wrap items-center gap-2">
                  {p === 'github' && c?.oauthClientId && (
                    <Button size="sm" onPress={() => void connectGithub(c).catch((e: Error) => setError(e.message))}>
                      Connect with GitHub
                    </Button>
                  )}
                  {p === 'azure-devops' && (
                    <Button size="sm" onPress={() => void run(() => routes.connectAzureDevops(c?.id))}>
                      Connect through Entra
                    </Button>
                  )}
                  {p === 'jira' && (
                    <input
                      aria-label="Atlassian account email"
                      className={`${inputClass} max-w-xs`}
                      type="email"
                      value={jiraEmail}
                      onChange={(e) => setJiraEmail(e.target.value)}
                      placeholder="Your Atlassian account email"
                      autoComplete="email"
                    />
                  )}
                  <input
                    aria-label={`${PROVIDER_TEXT[p]} personal token`}
                    className={`${inputClass} mono min-w-0 flex-1 basis-60`}
                    type="password"
                    value={tokens[p] ?? ''}
                    onChange={(e) => setTokens({ ...tokens, [p]: e.target.value })}
                    placeholder={p === 'github' ? 'Or paste a fine-grained personal access token' : p === 'jira' ? 'Paste an Atlassian API token' : 'Paste a personal access token (Code read & write, Work items read)'}
                    autoComplete="off"
                  />
                  <Button
                    size="sm"
                    variant="secondary"
                    isDisabled={(tokens[p] ?? '').trim().length < 8 || (p === 'jira' && !jiraEmail.includes('@'))}
                    onPress={() => void run(() => routes.linkSourceIdentity(p, { token: tokens[p]!.trim(), connectionId: c?.id, ...(p === 'jira' ? { email: jiraEmail.trim() } : {}) }).then(() => setTokens({ ...tokens, [p]: '' })))}
                  >
                    Link
                  </Button>
                </div>
              )}
            </ListRow>
          );
        })}
        {error ? (
          <ListBlock>
            <ErrorNote error={error} />
          </ListBlock>
        ) : null}
      </PlainPanel>
    </div>
  );
}
